import {createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '../../store';
import { plansApi } from '../../services/plans';
import { PlanListItem, ResourceListItem, PlanImageItem } from '../../types';
import { planResourcesApi } from '../../services/planResources';
import { PlanDetails, PlanTagWithName } from './types';
import { planImportApi } from '../../services/planImport';
import { planTagsApi } from '../../services/planTags';
import { planImagesApi } from '../../services/planImages';

interface PlanState {
	planListItems: PlanListItem[];
	planDetails: PlanDetails | null;
	owned: PlanListItem[];
	favourites: PlanListItem[];
}

const initialState: PlanState = {
	planListItems: [],
	planDetails: null,
	owned: [],
	favourites: [],
};

export const planSlice = createSlice({
	name: 'plans',
	initialState,
	reducers: {
		planListItemsSetState(state: PlanState, action) {
			state.planListItems = action.payload;
			return state;
		},
		planOwnedSetState(state: PlanState, action) {
			state.owned = action.payload;
			return state;
		},
		planDetailsSetState(state: PlanState, action) {
			state.planDetails = action.payload;
			return state;
		},
		planResourceAddItem(state: PlanState, action) {
			const newResources = [...(state.planDetails as PlanDetails).resources, action.payload];
			state.planDetails = {...(state.planDetails as PlanDetails), resources: newResources};
		},
		planResourceRemoveItem(state: PlanState, action) {
			const newResources = (state.planDetails as PlanDetails).resources.filter(({ id }) => id !== action.payload);
			state.planDetails = {...(state.planDetails as PlanDetails), resources: newResources};
		},
		planResourceUpdateItem(state: PlanState, action) {
			const newResources = (state.planDetails as PlanDetails).resources.map((resource) => resource.id === action.payload.id ? {...resource, ...action.payload} : resource);
			state.planDetails = {...(state.planDetails as PlanDetails), resources: newResources};
		},
		favouritesSetState(state: PlanState, action) {
			state.favourites = action.payload;
			return state;
		},
		favouritesAddItem(state: PlanState, action) {
			state.favourites = [action.payload, ...state.favourites];
			return state;
		},
		favouritesRemoveItem(state: PlanState, action) {
			state.favourites = state.favourites.filter(({ id }) => id !== action.payload);
			return state;
		}
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(plansApi.endpoints.planGetInfo.matchFulfilled, (state: PlanState, action: PayloadAction<PlanDetails>) => {
				planSlice.caseReducers.planDetailsSetState(state, planSlice.actions.planDetailsSetState(action.payload));
			})
			.addMatcher(plansApi.endpoints.planResourceAdd.matchFulfilled, (state: PlanState, action: PayloadAction<ResourceListItem>) => {
				planSlice.caseReducers.planResourceAddItem(state, planSlice.actions.planResourceAddItem(action.payload));
			})
			.addMatcher(planResourcesApi.endpoints.planResourceDelete.matchFulfilled, (state: PlanState, action: PayloadAction<Pick<ResourceListItem, 'id'>>) => {
				planSlice.caseReducers.planResourceRemoveItem(state, planSlice.actions.planResourceRemoveItem(action.payload.id));
			})
			.addMatcher(plansApi.endpoints.plansList.matchFulfilled, (state: PlanState, action: PayloadAction<PlanListItem[]>) => {
				planSlice.caseReducers.planOwnedSetState(state,	planSlice.actions.planOwnedSetState(action.payload));
			})
			.addMatcher(plansApi.endpoints.loadFavourites.matchFulfilled, (state, action) => {
				planSlice.caseReducers.favouritesSetState(state, planSlice.actions.favouritesSetState(action.payload));
			})
			.addMatcher(plansApi.endpoints.addFavourite.matchFulfilled, (state, action) => {
				planSlice.caseReducers.favouritesAddItem(state, planSlice.actions.favouritesAddItem(action.payload));
			})
			.addMatcher(plansApi.endpoints.removeFavourite.matchFulfilled, (state, action) => {
				planSlice.caseReducers.favouritesRemoveItem(state, planSlice.actions.favouritesRemoveItem(action.payload));
			})
			.addMatcher(planImagesApi.endpoints.planImageUpload.matchFulfilled, (state: PlanState, action: PayloadAction<PlanImageItem>) => {
				if (state.planDetails) {
					state.planDetails = { ...state.planDetails, images: [...state.planDetails.images, action.payload] };
				}
			})
			.addMatcher(planImagesApi.endpoints.planImageDelete.matchFulfilled, (state: PlanState, action: PayloadAction<Pick<PlanImageItem, 'id'>>) => {
				if (state.planDetails) {
					state.planDetails = { ...state.planDetails, images: (state.planDetails.images ?? []).filter((image) => image.id !== action.payload.id) };
				}
			})
			.addMatcher(planImportApi.endpoints.planImportFromYT.matchFulfilled, (state, action) => {
				state.owned = [action.payload, ...state.owned];
			})
			.addMatcher(planImportApi.endpoints.planImportFromIG.matchFulfilled, (state, action) => {
				state.owned = [action.payload, ...state.owned];
			})
			.addMatcher(planImportApi.endpoints.planImportFromPinterest.matchFulfilled, (state, action) => {
				state.owned = [action.payload, ...state.owned];
			})
			.addMatcher(plansApi.endpoints.planDelete.matchFulfilled, (state, action) => {
				state.owned = state.owned.filter((plan) => plan.id !== action.payload);
			})
			.addMatcher(plansApi.endpoints.planCreate.matchFulfilled, (state, action) => {
				state.owned = [action.payload, ...state.owned];
			})
			// the following is only necessary for the "reset" button to work properly after an update has been submitted, because the formData will be based on the entity that is loaded into memory. Should be removed if edit form is moved into a modal.
			.addMatcher(plansApi.endpoints.planUpdate.matchFulfilled, (state, action: PayloadAction<PlanDetails>) => {
				state.planDetails = {...state.planDetails, ...action.payload} as PlanDetails;
			})
			.addMatcher(planTagsApi.endpoints.planTagsCreate.matchFulfilled, (state, action) => {
				state.planDetails = state.planDetails ? {...state.planDetails, tags: [...(state.planDetails as PlanDetails).tags, action.payload]} : null;
			})
			.addMatcher(planTagsApi.endpoints.planTagsDelete.matchFulfilled, (state, action) => {
				state.planDetails = state.planDetails ? {...state.planDetails, tags: (state.planDetails as PlanDetails).tags.filter((planTag) => planTag.id !== action.payload.id)} : null;
			})
			.addMatcher(plansApi.endpoints.planVideoUpload.matchFulfilled, (state, action) => {
				state.planDetails = state.planDetails ? {...state.planDetails, videos: [...(state.planDetails as PlanDetails).videos, action.payload]} : null;
			})
			.addMatcher(plansApi.endpoints.planVideoDelete.matchFulfilled, (state, action) => {
				state.planDetails = state.planDetails ? {...state.planDetails, videos: (state.planDetails as PlanDetails).videos.filter((video) => video.id !== action.payload.id)} : null;
			})
			.addMatcher(planImagesApi.endpoints.planImagesReorder.matchFulfilled, (state, action: PayloadAction<Pick<PlanImageItem, 'id' | 'rank'>[]>) => {
				const ranksByIds = new Map<number, number>(action.payload.map((item) => [item.id, item.rank]));
				const orderedImages = (state.planDetails as PlanDetails).images.sort((a, b) => ranksByIds.get(a.id)! - ranksByIds.get(b.id)!); // eslint-disable-line @typescript-eslint/no-non-null-assertion
				state.planDetails = state.planDetails ? {...state.planDetails, images: orderedImages ?? []} : null;
			})
			.addMatcher(planTagsApi.endpoints.planTagsCreate.matchFulfilled, (state, action: PayloadAction<PlanTagWithName>) => {
				state.planDetails = state.planDetails ? {...state.planDetails, tags: [...state.planDetails.tags, action.payload ]} : null;
			});
	},
});

export const {
	planListItemsSetState,
	planDetailsSetState,
	favouritesSetState,
	favouritesAddItem,
	favouritesRemoveItem,
} = planSlice.actions;

export const selectPlansState = (state: RootState) => state.plans;

export const selectPlanListItems = createSelector([selectPlansState], (state) => state.planListItems);

export const selectPlanDetails = createSelector([selectPlansState], (state) => state.planDetails ?? null);

export const selectPlanAuthor = createSelector([selectPlansState], (state) => state.planDetails?.author ?? null);

export const selectPlanVendors = createSelector([selectPlansState], (state) => state.planDetails?.vendors ?? []);

export const selectPlanImages = createSelector([selectPlansState], (state) => state.planDetails?.images ?? []);

export const selectPlanVideos = createSelector([selectPlansState], (state) => state.planDetails?.videos ?? []);

export const selectPlanResources = createSelector([selectPlansState], (state) => state.planDetails?.resources ?? []);

export const selectPlanResourcesMaterials = createSelector([selectPlanResources], (resources) => resources.filter(({isTool}) => !isTool));

export const selectPlanResourcesTools = createSelector([selectPlanResources], (resources) => resources.filter(({isTool}) => isTool));

export const selectFavourites = createSelector([selectPlansState], (state: PlanState) => state.favourites);

export const selectPlansOwned = createSelector([selectPlansState], (state: PlanState) => state.owned);

export default planSlice.reducer;
